import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, RouteProps, Switch, useHistory } from 'react-router-dom';
import { RootState } from '../../reducers/reducers';
import routes from '../../routes';
import LoginView from '../LoginView';
import styles from '../styles/appStyles';
import EmailVerificationComponent from './EmailVerification';
import SignupComponent from './SignupComponent';
import SSOLogin from './SSOLogin';
import TwoFactorAuth from './TwoFactorAuth';
import Text from 'antd/lib/typography/Text';
import Layout from 'antd/lib/layout';
import LogoWordmark from '../../../assets/wordmark-spine.svg';
import RunwayLogo from '../../../assets/runway-wordmark.svg';
import orgStyles from '../styles/organizationNavigatorStyles';
import qs from 'query-string';
import { displayTextForForgotPasswordError } from '../../helpers/displayHelpers';
import { ForgotPasswordError } from '../../types';
import { tryGetAppConfig, TRY_GET_APP_CONFIG } from '../../actions/appConfig';
import { selectIsSSOSAMLLoginRequired } from '../../selectors/selectors';
import Spin from 'antd/lib/spin';
import { globalMessage } from '../../utils/globalMessage';
import { isMobileOnly } from 'react-device-detect';
import { useIsLoading } from '@/utils/hooks/helpers';

const AuthRoutesContainer: React.FC<RouteProps> = (props: RouteProps) => {
    const { location } = props;
    const [appConfigFetchAttempted, setAppConfigFetchAttempted] = React.useState(false);
    const dispatch = useDispatch();

    const isSSOSAMLRequired = useSelector((state: RootState) => selectIsSSOSAMLLoginRequired(state));
    const appConfigIsLoading = useIsLoading(TRY_GET_APP_CONFIG);

    const queryParams = qs.parse(props.location?.search);
    const isAdmin = queryParams.runwayAdmin;

    React.useEffect(() => {
        dispatch(tryGetAppConfig());
        setAppConfigFetchAttempted(true);
    }, []);

    if (!appConfigFetchAttempted) {
        return null;
    }

    if (appConfigIsLoading) {
        return (
            <div
                style={{
                    ...styles.loginContainer,
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Spin />
            </div>
        );
    }

    if (
        isSSOSAMLRequired === true &&
        (location.pathname === routes.login || location.pathname === routes.signup) &&
        !isAdmin
    ) {
        // sso-saml sign in & sign up required, force redirect to sso login page
        console.debug(
            '[REDIRECT DEBUG]',
            'current route:',
            location.pathname,
            '-> next route:',
            routes.sso,
            '(SSO SAML required)',
        );
        return <Redirect to={{ pathname: routes.sso, state: location.state }} />;
    }

    return (
        <Switch>
            <Route exact path={routes.sso} component={SSOLogin} />
            <Route exact path={routes.login} component={LoginView} />
            <Route exact path={routes.twoFactorAuth} component={TwoFactorAuth} />
            <Route
                exact
                path={[routes.signup, routes.qsciSignup, routes.buildDistroSignup]}
                component={SignupComponent}
            />
            <Route exact path={routes.verifyEmail} component={EmailVerificationComponent} />
            <Route
                render={() => {
                    console.debug(
                        '[REDIRECT DEBUG]',
                        'current route: unknown/unmatched',
                        '-> next route:',
                        routes.login,
                        '(default auth fallback)',
                    );
                    return <Redirect to={routes.login} />;
                }}
            />
        </Switch>
    );
};

const AuthContainer: React.FC<RouteProps> = (props: RouteProps) => {
    const history = useHistory();

    const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
    const stateFetched = useSelector((state: RootState) => state.user !== null);

    if (isLoggedIn && stateFetched) {
        console.debug(
            '[REDIRECT DEBUG]',
            'current route: auth container',
            '-> next route:',
            routes.dashboard,
            '(logged in and state fetched)',
        );
        return <Redirect to={routes.dashboard} />;
    }

    const queryParameters = props.location ? qs.parse(props.location.search) : {};
    const fpError = queryParameters['fpError'];
    const evError = queryParameters['evError'];

    React.useEffect(() => {
        if (fpError) {
            const errorString = displayTextForForgotPasswordError(fpError as ForgotPasswordError);

            globalMessage.error(errorString);

            // clear authError query param & prevent back navigation
            history.replace(window.location.pathname);
        }
    }, [fpError]);

    React.useEffect(() => {
        if (evError) {
            globalMessage.error('Email verification failed. Please try again.');

            // clear authError query param & prevent back navigation
            history.replace(window.location.pathname);
        }
    }, [evError]);

    const orgNavbar = (
        <div style={orgStyles.containerStyle}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }}
            >
                <img src={LogoWordmark} alt="Runway" style={{ marginTop: 24 }} />
            </div>
        </div>
    );

    return (
        <Layout style={{ ...styles.loginContainer, flexDirection: 'row' }}>
            {!isMobileOnly && orgNavbar}
            <Layout
                style={{
                    ...styles.loginContainer,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 16,
                    gap: 16,
                    overflow: 'auto',
                }}
            >
                {isMobileOnly && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <img src={RunwayLogo} alt="Runway" />
                    </div>
                )}
                <AuthRoutesContainer {...props} />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: isMobileOnly ? 'center' : 'flex-start',
                        alignItems: 'center',
                        paddingBottom: 6,
                        paddingLeft: 12,
                    }}
                >
                    <Text type="secondary" style={{ textAlign: 'right' }}>
                        Version {PACKAGE_VERSION}
                    </Text>
                </div>
            </Layout>
        </Layout>
    );
};

export default AuthContainer;
